/**
 * Ticket note formatting — makes ConnectWise email notes readable.
 *
 * CW stores the raw email body on the ticket note: quoted reply chains,
 * signatures, `cid:` inline images, Proofpoint/Outlook safelinks and CAUTION
 * banners. Rendered as-is the note is unreadable (reviewers saw walls of
 * `![\[photo\]](cid:...)` and tracking URLs).
 *
 * This module is pure and deterministic: raw string in, display HTML out.
 *  - unwraps Proofpoint `urldefense` and Outlook `safelinks` to the real URL,
 *  - removes inline-image / cid placeholders and CAUTION banners,
 *  - cuts the quoted reply chain (keeps the newest message),
 *  - trims the trailing signature/contact block,
 *  - collapses whitespace and escapes everything before rendering.
 *
 * The original text is always returned so the UI can offer "show full email".
 */

export interface FormattedTicketNote {
  /** Cleaned, escaped HTML for display. */
  html: string;
  /** The note as it arrived (escaped HTML, no trimming). */
  originalHtml: string;
  /** Quoted reply/forward chain was removed. */
  trimmedQuoted: boolean;
  /** Trailing signature block was removed. */
  trimmedSignature: boolean;
}

const MAX_INPUT = 40000;

/** Decode the handful of entities that show up in CW-pasted email text. */
function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&amp;/gi, "&");
}

/** Light HTML -> text: keep line and list structure, drop every tag. */
function htmlToText(raw: string): string {
  if (!/<[a-z!/][^>]*>/i.test(raw)) return raw;
  return decodeEntities(
    raw
      .replace(/<(script|style|head)[\s\S]*?<\/\1>/gi, " ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|tr|h[1-6]|blockquote)>/gi, "\n")
      .replace(/<li[^>]*>/gi, "\n• ")
      .replace(/<\/td>/gi, " ")
      .replace(/<[^>]+>/g, ""),
  );
}

/** Unwrap nam*.safelinks.protection.outlook.com and urldefense.proofpoint.com. */
export function unwrapSafeLink(url: string): string {
  const trimmed = url.trim();

  // Outlook SafeLinks: https://nam10.safelinks.protection.outlook.com/?url=<enc>&data=...
  if (/safelinks\.protection\.outlook\.com/i.test(trimmed)) {
    const match = /[?&]url=([^&]+)/i.exec(trimmed);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  // Proofpoint: https://urldefense.proofpoint.com/v2/url?u=https-3A__example.com&d=...
  if (/urldefense\.proofpoint\.com/i.test(trimmed)) {
    const match = /[?&]u=([^&]+)/i.exec(trimmed);
    if (match?.[1]) {
      let value = match[1];
      try {
        value = decodeURIComponent(value);
      } catch {
        /* keep as-is */
      }
      // Proofpoint escapes reserved characters as -XX and "/" as "_".
      value = value.replace(/-([0-9A-Fa-f]{2})/g, (_m, hex: string) =>
        String.fromCharCode(parseInt(hex, 16)),
      );
      value = value.replace(/^https?:_+/i, (m) => m.replace(/_+/g, "//"));
      // Drop Proofpoint's own tracking parameters from the original URL.
      const cut = value.search(/[?&](d|c|r|m|s|e)=/);
      if (cut > 0) value = value.slice(0, cut);
      return value.replace(/\s+$/, "");
    }
  }

  return trimmed;
}

/** Remove inline image placeholders (markdown-style cid refs and alt text). */
function stripInlineImages(text: string): string {
  return text
    // Markdown images, whatever the source: ![alt](cid:…) and ![alt](https://…).
    .replace(/!\[[^\]]*\]\((?:https?:)?[^)]*\)/g, " ")
    .replace(/\[(?:photo|image|icon|logo)[^\]]*\]\s*\(cid:[^)]*\)/gi, " ")
    .replace(/\(cid:[^)]*\)/gi, " ")
    .replace(/\bcid:[^\s)"']+/gi, " ")
    .replace(/\[(?:photo|image|icon|logo|cid)\]/gi, " ");
}

/** Remove the external-sender warning blocks that lead every CW email note. */
function stripCautionBanner(text: string): string {
  return text
    .replace(/^\s*CAUTION:.*$/gim, " ")
    .replace(/^\s*\*?\s*EXTERNAL(?: EMAIL)?\s*\*?.*$/gim, " ")
    .replace(/\bDo not click links or open attachments unless you (?:recognize|know).*?\./gi, " ");
}

const QUOTED_MARKERS: RegExp[] = [
  /^[ \t]*From:[^\n]*\n[ \t]*(?:Sent|Date):/im,
  /^[ \t]*-{2,}\s*Original Message\s*-{2,}/im,
  /^[ \t]*_{10,}\s*$/m,
  /^[ \t]*On [^\n]{5,120} wrote:\s*$/im,
  /^[ \t]*Sent from my [^\n]+$/im,
];

/** Cut the quoted reply chain; the newest message is what matters. */
function cutQuotedChain(text: string): { text: string; trimmed: boolean } {
  let cutAt = -1;
  for (const marker of QUOTED_MARKERS) {
    const match = marker.exec(text);
    if (match && match.index > 0) {
      cutAt = cutAt === -1 ? match.index : Math.min(cutAt, match.index);
    }
  }
  if (cutAt === -1) return { text, trimmed: false };
  return { text: text.slice(0, cutAt).trimEnd(), trimmed: true };
}

const CONTACT_LINE = [
  /\(?\b\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/, // phone
  /\b(?:ext|extension)\.?\s*\d+\b/i,
  /\b[A-Z]{2}\s+\d{5}(?:-\d{4})?\b/, // state + ZIP
  /\b(?:facebook|instagram|linkedin|twitter|x\.com)\b/i,
  /^https?:\/\/\S+$/i,
  // Titles appear mid-line in CW email signatures ("… Mark Gutierrez Community
  // Manager, Urban Office …"), so these are matched without ^ anchoring.
  /\b(?:community manager|technical support analyst|support analyst|service manager|account manager|director|president|owner|ceo)\b/i,
  /^(?:mobile|office|phone|direct|main|fax|web|email|e-mail)\s*[:.]/i,
  /^[\s|·•-]+$/,
];

const NAME_LINE = /^[A-Z][a-zA-Z.'-]+(?:\s+[A-Z][a-zA-Z.'-]+){1,3}$/;

/**
 * Trim the signature/contact block at the end of a note: phone numbers,
 * titles, addresses, social links and the sender's name. Stops at the first
 * real sentence, and never empties the note.
 */
function trimSignature(text: string): { text: string; trimmed: boolean } {
  const lines = text.split("\n");
  let i = lines.length - 1;
  let removed = 0;
  let cutMidLine = false;
  while (i >= 0 && removed < 15) {
    const line = lines[i] ?? "";
    const trimmedLine = line.trim();
    if (trimmedLine.length === 0) {
      i -= 1;
      removed += 1;
      continue;
    }
    // CW often appends the signature to the same line as the last sentence
    // ("… taking him off. Mark Gutierrez Community Manager … (832) …"). Cut at
    // the signature instead of throwing the whole line away.
    const cutAt = CONTACT_LINE.reduce((best, re) => {
      const m = re.exec(trimmedLine);
      return m && (best === -1 || m.index < best) ? m.index : best;
    }, -1);
    if (cutAt >= 0) {
      if (cutAt === 0) {
        lines.splice(i, 1);
        removed += 1;
        i -= 1;
        continue;
      }
      const prefix = trimmedLine
        .slice(0, cutAt)
        // A signature usually starts with the sender's name: drop a trailing name.
        .replace(/\s+[A-Z][a-zA-Z.'-]+(?:\s+[A-Z][a-zA-Z.'-]+){1,3}\s*$/, "")
        .replace(/\s+[A-Z]{2,}(?:\s+[A-Z]{2,}){1,3}\s*$/, "")
        .replace(/[\s*_]+$/, "")
        .trimEnd();
      if (prefix.length > 0) {
        lines[i] = prefix;
        cutMidLine = true;
      } else {
        lines.splice(i, 1);
      }
      removed += 1;
      break;
    }
    const isName = NAME_LINE.test(trimmedLine) && removed > 0;
    if (!isName) break;
    lines.splice(i, 1);
    removed += 1;
    i -= 1;
  }
  if (removed === 0) return { text, trimmed: false };
  const remaining = lines.join("\n").trimEnd();
  if (remaining.length === 0) return { text, trimmed: false };
  return { text: remaining, trimmed: true || cutMidLine };
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Escape, then turn bare URLs and emails into links (http/https/mailto only). */
function linkify(text: string): string {
  const escaped = escapeHtml(text);
  return escaped
    .replace(/\bhttps?:\/\/[^\s<]+[^\s<.,;:!?)]/gi, (url) => {
      const href = unwrapSafeLink(url);
      if (!/^https?:\/\//i.test(href)) return url;
      return `<a href="${escapeHtml(href)}" target="_blank" rel="noreferrer noopener">${url}</a>`;
    })
    .replace(/\b([\w.+-]+@(?:[\w-]+\.)+[a-z]{2,})\b/gi, (email) => {
      const clean = email.replace(/\.$/, "");
      return `<a href="mailto:${escapeHtml(clean)}">${clean}</a>`;
    });
}

function textToHtml(text: string): string {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (paragraphs.length === 0) return "";
  return paragraphs
    .map((p) => `<p>${p.split("\n").map((line) => linkify(line)).join("<br>")}</p>`)
    .join("");
}

/** Normalize emails/markdown links/CW quirks that make notes unreadable. */
function cleanText(raw: string): string {
  let text = htmlToText(raw.slice(0, MAX_INPUT));
  text = stripCautionBanner(text);
  // CW/markdown escapes brackets ("![\\[photo\\]]"), which defeats the image
  // stripping below unless the backslashes come off first.
  text = text.replace(/\\\[/g, "[").replace(/\\\]/g, "]");
  text = stripInlineImages(text);
  // CW prefixes the body with its own "<author><n>d ago" stamp, which the card
  // header already shows.
  text = text.replace(/^[ \t]*[A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+){0,3}\s*\d+\s*(?:s|m|h|d|w|mo|y)\s+ago[ \t]*\n/i, "");
  // Markdown emphasis CW keeps in note bodies (`**From:**`, `_site_`). Strip it
  // before the line heuristics run, or every "From:"/name line fails to match.
  text = text
    .replace(/\*\*/g, "")
    .replace(/\*([^*\n]{1,120})\*/g, "$1")
    .replace(/__/g, " ")
    .replace(/(^|\s)_([^_\n]{1,120})_(?=\s|$)/g, "$1$2");
  // Markdown links -> "label (url)" unless the label is the URL itself.
  text = text.replace(/\[([^\]]{1,120})\]\((https?:\/\/[^)\s]+)\)/g, (_m, label: string, url: string) =>
    label.trim() === url.trim() ? url : `${label.trim()} (${unwrapSafeLink(url)})`,
  );
  // Bare safelinks anywhere in the text.
  text = text.replace(/https?:\/\/(?:nam\d*\.)?safelinks\.protection\.outlook\.com\/\S+/gi, (url) =>
    unwrapSafeLink(url),
  );
  text = text.replace(/https?:\/\/urldefense\.proofpoint\.com\/\S+/gi, (url) => unwrapSafeLink(url));
  // Collapse runs of spaces/tabs and 3+ blank lines.
  text = text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n");
  return text.trim();
}

/**
 * Format one CW ticket note for display.
 *
 * Deterministic and side-effect free; the returned `originalHtml` keeps the
 * full (escaped) email so the UI can offer a "show full email" toggle.
 */
export function formatTicketNote(raw: string): FormattedTicketNote {
  const base = cleanText(raw ?? "");
  if (!base) return { html: "", originalHtml: "", trimmedQuoted: false, trimmedSignature: false };

  const quoted = cutQuotedChain(base);
  const signature = trimSignature(quoted.text);
  const cleaned = signature.text
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+$/gm, "")
    .trim();

  return {
    html: textToHtml(cleaned),
    originalHtml: textToHtml(base),
    trimmedQuoted: quoted.trimmed,
    trimmedSignature: signature.trimmed,
  };
}
